/**
 * Step 1 (Split by) derivations: API data and the step's draft → molecule props. Pure; each
 * function is unit tested in distributionSplitStep.viewModel.test.ts.
 */
import type { ReactNode } from "react";
import {
  DISTRIBUTION_AUDIENCE_COPY as AUDIENCE,
  DISTRIBUTION_ITEMS_COPY as ITEMS,
  DISTRIBUTION_VALUES_STATE_COPY as VALUES,
} from "../../../constants/distributionHelpContent";
import { andList, pluralize } from "../../../services/distributions/distributionFormat";
import { audienceOf } from "../../../services/distributions/distributionCopyMappers";
import { excludedItemIdsOf, type StoryItemState } from "../../../services/distributions/distributionItemSwitches";
import { splitFieldKey } from "../../../hooks/useSplitCheck";
import type {
  AudienceApi,
  DistributionApi,
  DistributionPatchApi,
  SplitFieldApi,
  SplitFieldOptionApi,
  TileKindApi,
  UncoveredPolicyApi,
  ValueKpiApi,
} from "../../../services/distributions/distributions.types";
import type { ValueRowView } from "../../../services/distributions/distributionView.types";
import type { StoryTileViews } from "../../../hooks/useStoryTileViews";
import type { WidgetThumbnailKind } from "../../atoms/WidgetThumbnail";
import type { RadioCardOption } from "../../molecules/RadioCardGroup";
import type { DistributionItemCard, DistributionItemPill } from "../../molecules/DistributionTilePlanCard";
import type { DistributionValueRow } from "../../molecules/DistributionValueList";
import type { TileView } from "../../molecules/TilePreview";
import { SPLIT_STEP_PREVIEW_COPY as PREVIEW } from "./distributionSplitStep.copy";

/** Above this many copies, step 1 warns before anyone schedules it. */
export const HIGH_COUNT_THRESHOLD = 50;

/** The step's editable state; saved through POST (first time) and PATCH. */
export interface SplitStepDraft {
  name: string;
  /**
   * Who receives the copies; null on a new distribution until the owner chooses (never
   * preselected). Saved in `content`; it decides which fields can split and what copies mask.
   */
  audience: AudienceApi | null;
  split: SplitFieldApi | null;
  excludedValues: string[];
  skipEmptyValues: boolean;
  /**
   * "Include new values automatically". The contract stores exclusions only, so a value that first
   * shows up later is never excluded; what the owner chooses is what happens while nobody receives
   * it: hold its copy (included, waiting for a recipient) or skip it. That is `uncovered_policy`,
   * which step 2 also offers for today's values with no recipient.
   */
  uncoveredPolicy: UncoveredPolicyApi;
  sendAsIs: string[];
  /** Personalized items turned off (round 6); saved in `content.excluded_item_ids`. */
  excludedItemIds: string[];
}

export const draftFromDistribution = (distribution: DistributionApi | null): SplitStepDraft => ({
  name: distribution?.name ?? "",
  audience: audienceOf(distribution?.content),
  split: distribution?.split ?? null,
  excludedValues: distribution?.excluded_values ?? [],
  skipEmptyValues: distribution?.skip_empty_values ?? true,
  uncoveredPolicy: distribution?.uncovered_policy ?? "hold",
  sendAsIs: distribution?.send_unfiltered_item_ids ?? [],
  excludedItemIds: excludedItemIdsOf(distribution?.content),
});

/**
 * "People in Acme" and "People outside Acme", each with what its copies mask: read from the class
 * each audience's copies are redacted at for this owner, never assumed.
 */
export const audienceOptions = (
  organisation: string | null,
  icons: Partial<Record<AudienceApi, ReactNode>> = {},
): RadioCardOption[] => [
  { value: "inside", title: AUDIENCE.inside(organisation), description: AUDIENCE.explain.inside, icon: icons.inside },
  { value: "outside", title: AUDIENCE.outside(organisation), description: AUDIENCE.explain.outside, icon: icons.outside },
];

/** A radio's value back as an audience. */
export const toAudience = (value: string): AudienceApi => (value === "inside" ? "inside" : "outside");

/** One field the story can be split by, shaped for display. */
export interface DistributionFieldOption {
  key: string;
  label: string;
  description: string | null;
  /** Where it reads from, e.g. "dim_store.region". */
  sourceRef: string | null;
  /** "5 values", once the value count is known. */
  countLabel?: string | null;
  /** "High count: 126 copies". */
  warning?: string | null;
  /** Listed but can't be chosen. */
  disabled?: boolean;
  disabledReason?: string | null;
}

/**
 * The listed fields as options, named by their display label (which adds the table when two share
 * a label). A field above the audience's clearance stays listed, disabled, with the server's reason.
 */
export const toFieldOptions = (
  fields: readonly SplitFieldOptionApi[],
  selectedKey: string | null,
  selectedValueCount: number | null,
): DistributionFieldOption[] =>
  fields.map((field) => {
    const key = splitFieldKey(field);
    const counted = key === selectedKey && selectedValueCount !== null;
    return {
      key,
      label: field.display_label || field.label,
      description: field.description,
      sourceRef: field.source_ref,
      countLabel: counted ? pluralize(selectedValueCount as number, "value", "values") : null,
      warning:
        counted && (selectedValueCount as number) > HIGH_COUNT_THRESHOLD
          ? `High count: ${selectedValueCount} copies`
          : null,
      disabled: field.above_clearance,
      disabledReason: field.above_clearance ? field.blocked_reason : null,
    };
  });

/** Include state per value: excluded on purpose, or empty while "Skip values with no data" is on. */
export const toValueListRows = (
  rows: readonly ValueRowView[],
  excludedValues: readonly string[],
  skipEmptyValues: boolean,
): DistributionValueRow[] =>
  rows.map((row) => ({
    value: row.value,
    statsLabel: row.statsLabel || null,
    expression: row.expression,
    empty: row.empty,
    locked: skipEmptyValues && row.empty,
    included: !excludedValues.includes(row.value) && !(skipEmptyValues && row.empty),
  }));

export type ValueFilter = "all" | "included" | "nodata";

/** Search plus the All · Included · No data filter, in data order. */
export const filterValueRows = (
  rows: readonly DistributionValueRow[],
  query: string,
  filter: ValueFilter,
): DistributionValueRow[] => {
  const q = query.trim().toLowerCase();
  return rows.filter(
    (row) =>
      (!q || row.value.toLowerCase().includes(q)) &&
      (filter === "all" || (filter === "included" ? row.included : row.empty)),
  );
};

export const includedCount = (rows: readonly DistributionValueRow[]): number =>
  rows.filter((row) => row.included).length;

export const copiesSummary = (count: number, fieldLabel: string): { strong: string; rest: string } => ({
  strong: pluralize(count, "personalized copy", "personalized copies"),
  rest: `, one for each ${fieldLabel} value you keep. Each copy only queries its own ${fieldLabel}.`,
});

/** The policy for a value that first shows up later (`uncovered_policy`), and the plain "no one" sentence. */
export const newValuesCopy = (
  fieldLabel: string,
  fieldPlural: string,
  hold: boolean,
): { label: string; detail: string; noOne: string } => ({
  label: `When a new ${fieldLabel.toLowerCase()} appears later`,
  detail: hold
    ? `Its copy is made and waits until someone is assigned to it. The run notice reminds you.`
    : `It is skipped until someone is assigned to it.`,
  noOne: `${fieldPlural} with no one get no copy.`,
});

export type ValuesStateAction = "switch_audience" | "retry" | "choose_field";

export interface ValuesStateView {
  tone: "warning" | "error" | "info";
  title: string;
  text: string | null;
  /** In order; the first is the main way out. */
  actions: ValuesStateAction[];
}

/**
 * Why the values area has nothing to choose from (round 6 `n1Hidden`, `n1Failed`): the chosen field
 * is above this audience's clearance (switching audience is offered only when the field fits the
 * other audience), the values couldn't be listed, or the field has none. Null when the list shows.
 */
export const toValuesState = (input: {
  fieldLabel: string;
  audienceShort: string;
  /** The chosen field is above this audience's clearance: why ("" when the server gave no reason). */
  hiddenReason: string | null;
  /** "people in Acme" when the field fits the other audience's clearance; null otherwise. */
  switchAudienceTo: string | null;
  errorMessage: string | null;
  isLoading: boolean;
  valueCount: number;
}): ValuesStateView | null => {
  if (input.hiddenReason !== null) {
    return {
      tone: "warning",
      title: VALUES.hiddenTitle(input.fieldLabel, input.audienceShort),
      text: input.hiddenReason || null,
      actions: input.switchAudienceTo ? ["switch_audience", "choose_field"] : ["choose_field"],
    };
  }
  if (input.errorMessage) {
    return { tone: "error", title: VALUES.failedTitle(input.fieldLabel), text: input.errorMessage, actions: ["retry", "choose_field"] };
  }
  if (!input.isLoading && input.valueCount === 0) {
    return { tone: "info", title: VALUES.emptyTitle(input.fieldLabel), text: VALUES.emptyText, actions: ["choose_field"] };
  }
  return null;
};

/** "126 copies is a lot. …" for fields with many values. */
export const highCountWarning = (count: number, fieldLabel: string): string | null =>
  count > HIGH_COUNT_THRESHOLD
    ? `${count} copies is a lot. Check that every ${fieldLabel.toLowerCase()} has someone to receive its copy before you schedule this.`
    : null;

// ─── What each copy includes (round 6 item switches) ─────────────────────────

const THUMB_KIND: Readonly<Record<TileKindApi, WidgetThumbnailKind>> = {
  kpi: "kpi",
  chart: "chart",
  table: "table",
  note: "text",
  link: "link",
  bi_view: "embed",
  still: "image",
};

const asIsNote = (kind: TileKindApi): string =>
  kind === "note" || kind === "link" || kind === "still" ? ITEMS.asIsNote[kind] : ITEMS.asIsNote.other;

/** A KPI tile's figure for the previewed value, matched by name (the KPI name rule: its title, trimmed). */
const kpiFor = (title: string, kpis: readonly ValueKpiApi[]): ValueKpiApi | null =>
  kpis.find((kpi) => kpi.label.trim().toLowerCase() === title.trim().toLowerCase()) ?? null;

export interface ItemsSectionInput {
  states: readonly StoryItemState[];
  fieldLabel: string;
  /** "people outside Acme", for a partly masked tile's status. */
  audienceShort: string;
  /** The value whose KPI figures the thumbnails show, and its figures; null until they load. */
  previewValue: string | null;
  previewKpis: readonly ValueKpiApi[];
  /** The story's tiles drawn from their own data, by item id (useStoryTileViews). */
  storyTiles: StoryTileViews;
}

/** The kind a placeholder takes while an item's data loads. */
const PLACEHOLDER_KIND: Readonly<Record<TileKindApi, TileView["kind"]>> = {
  kpi: "kpi",
  chart: "chart",
  table: "table",
  note: "text",
  link: "link",
  bi_view: "unavailable",
  still: "image",
};

/** A tile with no data yet, shaped like its kind; drawn as a placeholder (TilePreview reads its kind and title). */
export const placeholderTile = (kind: TileKindApi, id: string, title: string): TileView => {
  switch (PLACEHOLDER_KIND[kind]) {
    case "kpi":
      return { kind: "kpi", id, title, value: null, delta: null, direction: null, comparison: null, masked: false };
    case "chart":
      return { kind: "chart", id, title, spec: {} };
    case "table":
      return { kind: "table", id, title, columns: [], rows: [] };
    case "text":
      return { kind: "text", id, title, text: "" };
    case "link":
      return { kind: "link", id, title, url: "" };
    case "image":
      return { kind: "image", id, title, src: null };
    default:
      return { kind: "unavailable", id, title, reason: "" };
  }
};

/**
 * What an item's thumbnail draws. A KPI the value stats or the split check mask, or a sensitive one,
 * stays masked (fail closed: the story's own figure is never shown for it); a KPI with the previewed
 * value's figure shows that copy's figure; anything else is the story's tile from its own data. Until
 * that arrives, a placeholder of its kind; when the story's tiles can't be read, the backend's reason.
 */
export const toItemPreview = (
  state: StoryItemState,
  kpi: ValueKpiApi | null,
  storyTiles: StoryTileViews,
): Pick<DistributionItemCard, "preview" | "previewLoading"> => {
  const { tile, leftOut } = state;
  const id = tile.item_id;
  if (tile.kind === "kpi" && (kpi?.masked || leftOut?.reason === "masked" || tile.sensitive)) {
    return {
      preview: { kind: "kpi", id, title: tile.title, value: null, delta: null, direction: null, comparison: null, masked: true },
      previewLoading: false,
    };
  }
  if (tile.kind === "kpi" && kpi) {
    return {
      preview: {
        kind: "kpi",
        id,
        title: tile.title,
        value: kpi.formatted,
        delta: kpi.delta_formatted,
        direction: kpi.direction,
        comparison: null,
        masked: false,
      },
      previewLoading: false,
    };
  }
  const drawn = storyTiles.tiles.get(id);
  if (drawn) return { preview: drawn, previewLoading: false };
  if (storyTiles.isLoading) return { preview: placeholderTile(tile.kind, id, tile.title), previewLoading: true };
  return {
    preview: { kind: "unavailable", id, title: tile.title, reason: storyTiles.errorMessage ?? PREVIEW.noPreview },
    previewLoading: false,
  };
};

/** One item's card: status, tone, reason and live preview from its switch state (addendum §3.5). */
export const toItemCard = (
  state: StoryItemState,
  { fieldLabel, audienceShort, previewValue, previewKpis, storyTiles }: Omit<ItemsSectionInput, "states">,
): DistributionItemCard => {
  const { tile, leftOut } = state;
  const kpi = tile.kind === "kpi" && previewValue ? kpiFor(tile.title, previewKpis) : null;
  const base = {
    itemId: tile.item_id,
    title: tile.title,
    kind: THUMB_KIND[tile.kind],
    kindLabel: ITEMS.kind[tile.kind],
    ...toItemPreview(state, kpi, storyTiles),
    on: state.on,
    switchable: state.switch !== null,
    switchLabel: ITEMS.switchLabel(tile.title),
    overlayLabel: null,
    overlayIcon: null,
    flagged: false,
  };
  if (state.switch === "personalized") {
    if (!state.on) return { ...base, status: ITEMS.turnedOff, tone: "muted", statusIcon: "turned_off", note: ITEMS.turnedOffNote };
    if (tile.sensitive) {
      return { ...base, status: ITEMS.maskedFor(audienceShort), tone: "warning", statusIcon: "masked", note: ITEMS.maskedNote, flagged: true };
    }
    return { ...base, status: ITEMS.filtered(fieldLabel), tone: "success", statusIcon: "filtered", note: null };
  }
  if (state.switch === "as_is") {
    return state.on
      ? { ...base, status: ITEMS.asIs, tone: "accent", statusIcon: "as_is", note: asIsNote(tile.kind) }
      : { ...base, status: ITEMS.leftOut, tone: "muted", statusIcon: null, note: leftOut?.message ?? null };
  }
  if (leftOut?.reason === "masked") {
    return { ...base, status: ITEMS.everyValueMasked, tone: "muted", statusIcon: "masked", note: leftOut.message };
  }
  const wholeStory = leftOut?.reason === "bi_view" || leftOut?.reason === "still";
  return {
    ...base,
    status: ITEMS.cantBeFiltered,
    tone: "muted",
    statusIcon: "locked",
    note: leftOut?.message ?? null,
    overlayLabel: wholeStory ? ITEMS.wouldShowEvery(fieldLabel) : null,
    overlayIcon: wholeStory ? "locked" : null,
  };
};

/** Counts behind the tally: in each copy, filtered, as is, left out and partly masked. */
export const itemsTally = (states: readonly StoryItemState[]) => {
  const on = states.filter((state) => state.on);
  return {
    total: states.length,
    included: on.length,
    filtered: on.filter((state) => state.switch === "personalized").length,
    asIs: on.filter((state) => state.switch === "as_is").length,
    leftOut: states.length - on.length,
    masked: on.filter((state) => state.switch === "personalized" && state.tile.sensitive).length,
    /** Personalized items exist, and every one is off: each copy would be the same. */
    noneFiltered: states.some((state) => state.switch === "personalized") && !on.some((state) => state.switch === "personalized"),
  };
};

/** The section's shaped props, apart from handlers and loading state. */
export const toItemsSection = (
  input: ItemsSectionInput,
): {
  tallyStrong: string;
  pills: DistributionItemPill[];
  note: string;
  warning: string | null;
  items: DistributionItemCard[];
} => {
  const tally = itemsTally(input.states);
  const items = input.states.map((state) => toItemCard(state, input));
  const pills: DistributionItemPill[] = [
    { key: "filtered", label: ITEMS.filteredPill(tally.filtered, input.fieldLabel), variant: "success" },
  ];
  if (tally.asIs) pills.push({ key: "as-is", label: ITEMS.asIsPill(tally.asIs), variant: "info" });
  if (tally.leftOut) pills.push({ key: "left-out", label: ITEMS.leftOutPill(tally.leftOut), variant: "neutral" });
  if (tally.masked) pills.push({ key: "masked", label: ITEMS.maskedPill(tally.masked), variant: "warning", masked: true });
  const showsFigures = items.some((item) => item.preview.kind === "kpi" && (item.preview.masked || item.preview.value));
  const showsStory = items.some((item) => !item.previewLoading && (item.preview.kind === "chart" || item.preview.kind === "table"));
  return {
    tallyStrong: ITEMS.tally(tally.included, tally.total),
    pills,
    note: [
      showsFigures && input.previewValue ? ITEMS.figuresNote(input.previewValue) : null,
      showsStory ? PREVIEW.storyNumbers : null,
      ITEMS.staysOut(input.fieldLabel),
    ]
      .filter(Boolean)
      .join(" "),
    warning: tally.noneFiltered ? ITEMS.noPersonalItem(input.fieldLabel) : null,
    items,
  };
};

/** Toggles one value in the excluded list. */
export const toggleExcluded = (excluded: readonly string[], value: string, included: boolean): string[] =>
  included ? excluded.filter((entry) => entry !== value) : excluded.includes(value) ? [...excluded] : [...excluded, value];

const sameList = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((entry, index) => entry === b[index]);

/**
 * Only the keys that differ from the saved distribution. Each top-level key replaces wholesale, so
 * the audience and the turned-off items go with the rest of the saved content; `saveAudience`
 * sends the audience even when it reads the same (a new draft: create takes no audience).
 */
export const buildSplitPatch = (
  draft: SplitStepDraft,
  saved: DistributionApi,
  saveAudience = false,
): DistributionPatchApi | null => {
  const patch: DistributionPatchApi = {};
  const audienceChanged = Boolean(draft.audience && (saveAudience || draft.audience !== audienceOf(saved.content)));
  const itemsChanged = !sameList(draft.excludedItemIds, excludedItemIdsOf(saved.content));
  if (audienceChanged || itemsChanged) {
    patch.content = { ...saved.content };
    if (draft.audience) patch.content.audience = draft.audience;
    if (itemsChanged) patch.content.excluded_item_ids = [...draft.excludedItemIds];
  }
  if (draft.name.trim() && draft.name.trim() !== saved.name) patch.name = draft.name.trim();
  if (draft.split && (!saved.split || splitFieldKey(draft.split) !== splitFieldKey(saved.split))) patch.split = draft.split;
  if (!sameList(draft.excludedValues, saved.excluded_values)) patch.excluded_values = draft.excludedValues;
  if (draft.skipEmptyValues !== saved.skip_empty_values) patch.skip_empty_values = draft.skipEmptyValues;
  if (draft.uncoveredPolicy !== saved.uncovered_policy) patch.uncovered_policy = draft.uncoveredPolicy;
  if (!sameList(draft.sendAsIs, saved.send_unfiltered_item_ids)) patch.send_unfiltered_item_ids = draft.sendAsIs;
  return Object.keys(patch).length ? patch : null;
};
